/**
 * Fixed Assets Landing — IT Depreciation
 * ----------------------------------------------------------
 * Inline classification flow (no modal):
 *   • Books JSON ingest → server auto-classifies each ledger by name+subhead
 *   • Workbench shows Ledger | Subhead | Closing | Adds | Credits | IT Block (dropdown)
 *   • Auditor changes the dropdown directly to override the auto-suggestion
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FolderUp, Loader2, Plus, Trash2, Wrench, Search, BookOpen, FileText, ArrowDown, Calculator, LayoutGrid, History, Settings2, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import AdditionsTab from "@/pages/fixed_assets/AdditionsTab";
import CreditsTab from "@/pages/fixed_assets/CreditsTab";
import ComputeTab from "@/pages/fixed_assets/ComputeTab";
import SummaryTab from "@/pages/fixed_assets/SummaryTab";
import GenerationsDrawer from "@/components/GenerationsDrawer";
import DivisionSettingsModal from "@/pages/fixed_assets/DivisionSettingsModal";
import { DEFAULT_FY } from "@/lib/fy";
import { readScopeFromUrl, scopeRequestPayload } from "@/lib/scope";
import ScopeChip from "@/components/ScopeChip";
import FAConsolidationStrip from "@/components/FAConsolidationStrip";
import useScopedRuns from "@/hooks/useScopedRuns";

const inr = (v) => {
  const n = Number(v || 0);
  if (!n) return "–";
  const s = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
};

export default function FixedAssetsLanding() {
  const { clientId, rid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlScope = readScopeFromUrl(location.search);

  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingFor, setSavingFor] = useState(null); // ledger_id when saving
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | unclassified | classified
  const [tallyGroupFilter, setTallyGroupFilter] = useState("all");
  const [subheadFilter, setSubheadFilter] = useState("all");
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBlock, setBulkBlock] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);
  const [tab, setTab] = useState("ledgers"); // ledgers | additions | credits | compute | summary
  const [auditFilter, setAuditFilter] = useState(null); // optional audit-flag filter passed to AdditionsTab
  const [showHistory, setShowHistory] = useState(false);
  const [showAdSettings, setShowAdSettings] = useState(false);
  const dropRef = useRef(null);
  // Phase C.2 — divisions list for ConsolidationStrip.
  const [divisions, setDivisions] = useState([]);
  useEffect(() => {
    if (!clientId) return;
    http.get(`/clients/${clientId}`)
      .then(({ data }) => setDivisions(data?.divisions || []))
      .catch(() => setDivisions([]));
  }, [clientId]);

  // Cross-tab navigation helper invoked by Summary's audit-flag cards.
  const goToFilteredAdditions = (flagKey) => {
    if (flagKey === "discount_pending") {
      // Discount pending lives on the Credits tab (different shape)
      setAuditFilter(null);
      setTab("credits");
      return;
    }
    setAuditFilter(flagKey);
    setTab("additions");
  };

  /* --- Initial loads --- */
  useEffect(() => {
    if (!clientId) return;
    http.get(`/fixed-assets/runs?client_id=${clientId}`)
      .then(({ data }) => setRuns(data?.rows || []))
      .catch(() => {});
    http.get(`/fixed-assets/blocks`)
      .then(({ data }) => setBlocks(data?.rows || []))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (!rid) { setRun(null); setLedgers([]); return; }
    refreshRun();
    refreshLedgers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);

  // Backfill auto-classification for legacy runs that were ingested before
  // the auto-classifier existed. Triggers exactly once per run when we see
  // ledgers loaded but ALL have empty block_label.
  const backfilledRef = useRef(new Set());
  useEffect(() => {
    if (!rid || !ledgers.length) return;
    if (backfilledRef.current.has(rid)) return;
    const allEmpty = ledgers.every(L => !L.block_label);
    if (!allEmpty) return;
    backfilledRef.current.add(rid);
    http.post(`/fixed-assets/runs/${rid}/auto-classify-pending`)
      .then(({ data }) => {
        if (data?.classified > 0) {
          toast.success(`Auto-classified ${data.classified} of ${ledgers.length} ledgers — review and override as needed.`);
          refreshRun();
          refreshLedgers();
        }
      })
      .catch(() => {});
  }, [rid, ledgers]);

  const refreshRun = async () => {
    const { data } = await http.get(`/fixed-assets/runs/${rid}`);
    setRun(data);
  };
  const refreshLedgers = async () => {
    const { data } = await http.get(`/fixed-assets/runs/${rid}/ledgers`);
    setLedgers(data?.rows || []);
  };

  /* --- Run create / delete --- */
  const createRun = async () => {
    const fy = window.prompt("Enter Financial Year (e.g., 2025-26):", urlScope.fy || DEFAULT_FY);
    if (!fy) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/fixed-assets/runs`, {
        client_id: clientId, fy,
        ...scopeRequestPayload(urlScope),
      });
      setRuns(rs => [data, ...rs]);
      toast.success(`Run created · FY ${data.fy}${data.scope_label ? " · " + data.scope_label : ""}`);
      navigate(`/dashboard/clients/${clientId}/utilities/fixed-assets/runs/${data.id}${location.search || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create run");
    } finally { setBusy(false); }
  };
  const deleteRun = async (id) => {
    if (!window.confirm("Delete this run? Ledgers, additions and credits will be removed.")) return;
    await http.delete(`/fixed-assets/runs/${id}`);
    setRuns(rs => rs.filter(x => x.id !== id));
    toast.success("Run deleted");
    if (id === rid) navigate(`/dashboard/clients/${clientId}/utilities/fixed-assets`);
  };

  /* --- Books JSON upload --- */
  const onIngest = async (file) => {
    if (!file || !rid) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/fixed-assets/runs/${rid}/ingest-books`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const auto = (data.summary?.confirmed || 0);
      toast.success(
        `${data.ledgers} FA ledgers · ${data.additions} additions · ${data.credits} credits · ` +
        `${auto} auto-classified, ${data.summary?.pending || 0} need review`
      );
      refreshRun(); refreshLedgers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Books ingest failed");
    } finally { setBusy(false); }
  };

  /* --- Ingest from Library (Layer 1: bridge for multi-division consolidation) ---
     Pulls one or more division-scoped Books JSONs from the Data Library and
     ingests them into this FA run.  When multiple sources exist the server
     merges them with a "{DivisionName} :: " ledger-name prefix so division
     provenance is preserved in the workbench.
  */
  const onIngestFromLibrary = async () => {
    if (!rid) return;
    setBusy(true);
    try {
      // Preview the sources first so the auditor knows exactly what
      // is about to be ingested.
      const { data: preview } = await http.get(`/fixed-assets/runs/${rid}/library-books-sources`);
      const sources = preview?.rows || [];
      if (sources.length === 0) {
        toast.error("No Books JSON found in the Data Library. Upload it first via the Data Library tab.");
        return;
      }
      const labels = sources.map(s => `• ${s.division_name} · ${s.filename} (v${s.version_no})`).join("\n");
      const msg = sources.length === 1
        ? `Ingest 1 source?\n\n${labels}`
        : `Ingest ${sources.length} sources and MERGE into a consolidated workbench?\n\n` +
          `Ledger names will be prefixed with the division name to preserve provenance.\n\n${labels}`;
      if (!window.confirm(msg)) return;

      const { data } = await http.post(`/fixed-assets/runs/${rid}/ingest-from-library`, {});
      const auto = data.summary?.confirmed || 0;
      const mergedMsg = data.merged ? ` · merged ${data.sources?.length || 0} divisions` : "";
      toast.success(
        `${data.ledgers} FA ledgers · ${data.additions} additions · ${data.credits} credits${mergedMsg} · ` +
        `${auto} auto-classified, ${data.summary?.pending || 0} need review`
      );
      refreshRun(); refreshLedgers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        ? (typeof detail === "string" ? detail : JSON.stringify(detail))
        : (status ? `Library ingest failed (HTTP ${status})` : `Library ingest failed: ${e?.message || "network error"}`);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  /* --- Phase D — Divisional sign-off --- */
  const markDivisionComplete = async () => {
    if (!rid || !run) return;
    const isDiv = run.scope_kind === "division";
    if (!isDiv) {
      toast.error("Mark Complete only applies to division-scoped runs.");
      return;
    }
    const nAdd = run.summary?.additions || 0;
    const nCr  = run.summary?.credits || 0;
    let status = "ready";
    let msg = `Mark this division as Ready?\n\n${nAdd} addition(s) and ${nCr} credit(s) captured.\n\nOnce marked, the consolidation will be able to compose this division.`;
    if (nAdd === 0 && nCr === 0) {
      status = "ready_no_activity";
      msg = `Mark this division as 'No FA Activity in FY ${run.fy}'?\n\nNo additions or credits were captured. This confirms there is genuinely no FA movement to record for this division.\n\nOnce marked, the consolidation will count this as a zero-row contribution.`;
    }
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await http.patch(`/fixed-assets/runs/${rid}/divisional-status`, { status });
      toast.success(`Division marked: ${data.status === "ready_no_activity" ? "No Activity" : "Ready"}`);
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update divisional status");
    } finally { setBusy(false); }
  };

  const reopenDivision = async () => {
    if (!rid || !window.confirm("Reopen this division for editing? Any pending consolidation will be marked stale and must be re-composed.")) return;
    setBusy(true);
    try {
      await http.patch(`/fixed-assets/runs/${rid}/divisional-status`, { status: "in_progress" });
      toast.success("Division reopened");
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen");
    } finally { setBusy(false); }
  };

  /* --- Inline block change --- */
  const setBlock = async (ledger_id, block_label) => {
    setSavingFor(ledger_id);
    // Optimistic UI
    setLedgers(rows => rows.map(r => r.fa_ledger_id === ledger_id
      ? { ...r, block_label, classification_status: block_label ? "confirmed" : "pending" }
      : r));
    try {
      await http.patch(`/fixed-assets/runs/${rid}/ledgers/${ledger_id}/block`, { block_label });
      // Quietly refresh run summary so the stats strip stays accurate
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save block");
      refreshLedgers();
    } finally {
      setSavingFor(null);
    }
  };

  /* --- Bulk block change (map many ledgers to one IT Block at once) --- */
  const toggleSelected = (ledgerId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(ledgerId) ? next.delete(ledgerId) : next.add(ledgerId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const allVisibleSelected = visibleLedgers.length > 0 &&
        visibleLedgers.every(r => prev.has(r.fa_ledger_id));
      const next = new Set(prev);
      if (allVisibleSelected) visibleLedgers.forEach(r => next.delete(r.fa_ledger_id));
      else visibleLedgers.forEach(r => next.add(r.fa_ledger_id));
      return next;
    });
  };

  const bulkApplyBlock = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !bulkBlock) return;
    setBulkApplying(true);
    try {
      const { data } = await http.post(`/fixed-assets/runs/${rid}/ledgers/bulk-block`, {
        fa_ledger_ids: ids, block_label: bulkBlock,
      });
      toast.success(`Mapped ${data.updated} ledger${data.updated === 1 ? "" : "s"} to ${bulkBlock}`);
      setSelectedIds(new Set());
      setBulkBlock("");
      await refreshLedgers();
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk mapping failed");
    } finally {
      setBulkApplying(false);
    }
  };

  /* --- Filtered rows --- */
  const visibleLedgers = useMemo(() => {
    let rows = ledgers;
    if (filter === "unclassified") rows = rows.filter(r => !r.block_label);
    else if (filter === "classified") rows = rows.filter(r => !!r.block_label);
    if (tallyGroupFilter !== "all") rows = rows.filter(r => (r.parent_group || "—") === tallyGroupFilter);
    if (subheadFilter !== "all") rows = rows.filter(r => (r.assureai_subhead || "Not mapped") === subheadFilter);
    if (mismatchOnly) rows = rows.filter(r => r.subhead_block_mismatch);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter(r => `${r.name} ${r.parent_group} ${r.assureai_subhead}`.toLowerCase().includes(q));
    return rows;
  }, [ledgers, search, filter, tallyGroupFilter, subheadFilter, mismatchOnly]);

  const tallyGroupOptions = useMemo(
    () => Array.from(new Set(ledgers.map(r => r.parent_group || "—"))).sort(),
    [ledgers],
  );
  const subheadOptions = useMemo(
    () => Array.from(new Set(ledgers.map(r => r.assureai_subhead || "Not mapped"))).sort(),
    [ledgers],
  );
  const mismatchCount = useMemo(
    () => ledgers.filter(r => r.subhead_block_mismatch).length,
    [ledgers],
  );

  /* --- Phase D bugfix · scope-filtered runs list + cross-scope guard ---
     Lifted into the ``useScopedRuns`` hook (2026-06-24) so Balance
     Confirmation and MSME can share the same invariant. */
  const { scopedRuns, showAllRuns, setShowAllRuns, isScopeMismatch } =
    useScopedRuns(runs, urlScope);
  const scopeMismatch = isScopeMismatch(run);

  const summary = run?.summary || {};
  const unclassifiedCount = summary.pending || 0;
  const classifiedCount = summary.confirmed || 0;

  /* ============================================================ */
  /* No-run state                                                 */
  /* ============================================================ */
  if (!rid) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-[#1A1A19]">
        <Header clientId={clientId}/>
        <div className="max-w-6xl mx-auto px-6 pt-8 pb-24">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">Income-tax Depreciation</div>
              <h1 className="font-heading text-3xl mt-1">Fixed Assets — Runs</h1>
              <p className="text-sm text-[#52524E] mt-1.5 max-w-2xl">
                Each run is a single financial year of depreciation working. Books JSON,
                classifications, additions and computations are scoped to the run.
              </p>
            </div>
            <button
              data-testid="fa-create-run"
              onClick={createRun}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-900 text-white text-[13px] hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
              New Run
            </button>
          </div>

          {scopedRuns.length === 0 ? (
            <div className="bg-white border border-[#E5E5E0] p-10 text-center">
              <Wrench size={28} className="mx-auto text-slate-400"/>
              <div className="mt-3 font-heading text-lg">
                {runs.length === 0
                  ? "No runs yet"
                  : `No runs for this scope (${urlScope.scopeLabel || urlScope.scopeKey || "current scope"})`}
              </div>
              <p className="text-sm text-[#52524E] mt-1">
                Click <strong>New Run</strong> to start the FY&apos;s depreciation working at this scope.
              </p>
              {runs.length > 0 && (
                <button
                  onClick={() => setShowAllRuns(true)}
                  data-testid="fa-show-all-runs"
                  className="mt-3 text-[11px] uppercase tracking-[0.14em] font-mono text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
                >
                  Show all runs across scopes ({runs.length})
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Phase D — FA-specific consolidation strip (per-division
                  status + Generate Consolidated button + stale tracker). */}
              <FAConsolidationStrip
                clientId={clientId}
                fy={urlScope.fy || DEFAULT_FY}
                divisions={divisions}
                scope={urlScope}
                consolidationRunId={
                  (runs.find(r => r.scope_kind === "consolidation"
                                   && (r.fy === (urlScope.fy || DEFAULT_FY))) || {}).id
                }
                onComposed={() => {
                  http.get(`/fixed-assets/runs?client_id=${clientId}`)
                    .then(({ data }) => setRuns(data?.rows || []))
                    .catch(() => {});
                }}
              />
              {showAllRuns && (
                <div className="mb-2 px-3 py-2 border border-slate-300 bg-slate-50 text-[11.5px] text-slate-700 flex items-center justify-between rounded-sm" data-testid="fa-show-all-banner">
                  <span>Showing all runs across scopes — including those not matching the current URL scope.</span>
                  <button
                    onClick={() => setShowAllRuns(false)}
                    className="text-[11px] uppercase tracking-[0.14em] font-mono text-slate-500 hover:text-slate-800"
                  >
                    Reset filter
                  </button>
                </div>
              )}
              <div className="bg-white border border-[#E5E5E0] divide-y divide-[#EDEDE7]">
              {scopedRuns.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-[#F9F9F8]">
                  <Link
                    to={`/dashboard/clients/${clientId}/utilities/fixed-assets/runs/${r.id}${location.search || ""}`}
                    data-testid={`fa-run-${r.id}`}
                    className="flex-1 min-w-0"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-700">FY {r.fy}</span>
                      {r.rolled_from_run_id && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                          Rolled forward
                        </span>
                      )}
                      <ScopeChip run={r} isMulti={(r.scope_label && r.scope_label !== "Consolidation")} />
                      <span className="font-heading text-base truncate">{r.name || "Untitled"}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {r.summary?.total_ledgers || 0} FA ledgers · {r.summary?.additions || 0} additions ·
                      {" "}{r.summary?.credits || 0} credits to classify ·
                      {" "}created {(r.created_at || "").slice(0, 10)}
                    </div>
                  </Link>
                  <button
                    data-testid={`fa-run-delete-${r.id}`}
                    onClick={() => deleteRun(r.id)}
                    className="text-rose-600 hover:bg-rose-50 p-1.5"
                    title="Delete run"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ============================================================ */
  /* In-run state                                                 */
  /* ============================================================ */
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1A1A19]">
      <Header clientId={clientId}/>
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-24">
        {/* Phase D bugfix · prominent scope banner — eliminates the
            "which division am I editing?" confusion that caused the
            Freelook bug.  Colour-coded: blue for consolidation, slate
            for division, amber when URL scope ≠ run scope. */}
        {run && (
          <div
            data-testid="fa-run-scope-banner"
            className={`mb-4 px-4 py-2.5 border rounded-sm flex items-center justify-between gap-3 ${
              scopeMismatch
                ? "border-amber-400 bg-amber-50"
                : run.scope_kind === "consolidation"
                  ? "border-emerald-300 bg-emerald-50/60"
                  : "border-slate-300 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className={`font-mono text-[10.5px] uppercase tracking-[0.18em] px-2 py-0.5 border rounded-sm ${
                scopeMismatch
                  ? "border-amber-500 bg-amber-100 text-amber-900"
                  : run.scope_kind === "consolidation"
                    ? "border-emerald-500 bg-emerald-100 text-emerald-900"
                    : "border-slate-500 bg-slate-200 text-slate-900"
              }`}>
                Scope · {run.scope_label || "Consolidation"}
              </span>
              <span className="text-[12.5px] text-slate-700 truncate">
                {run.summary?.total_ledgers || 0} FA ledgers · {run.summary?.additions || 0} additions · {run.summary?.credits || 0} credits
                {run.divisional_status === "ready" && " · Ready"}
                {run.divisional_status === "ready_no_activity" && " · No Activity"}
              </span>
            </div>
          </div>
        )}
        {/* Cross-scope warning — URL scope mismatches this run's scope. */}
        {scopeMismatch && (
          <div data-testid="fa-scope-mismatch-warning" className="mb-4 px-4 py-3 border border-amber-400 bg-amber-50 rounded-sm flex items-start gap-3">
            <div className="text-amber-900 mt-0.5"><Wrench size={14}/></div>
            <div className="text-[12.5px] text-amber-900 flex-1">
              <div className="font-medium">Scope mismatch — read-only mode</div>
              <div className="text-amber-800 mt-1">
                You&apos;re viewing a <strong>{run?.scope_label || "—"}</strong> run while the URL is set to <strong>{urlScope.scopeLabel || urlScope.scopeKey || "another scope"}</strong>.  Destructive actions (Ingest, Mark Complete, Compute) are disabled to prevent cross-division data corruption.
              </div>
              <div className="mt-2 flex items-center gap-3">
                <Link
                  to={`/dashboard/clients/${clientId}/utilities/fixed-assets?fy=${run?.fy || ""}&scope=${run?.scope_key || "consolidation"}`}
                  className="text-[11px] uppercase tracking-[0.14em] font-mono text-sky-700 hover:text-sky-900"
                >
                  ← Back to runs list ({run?.scope_label || "run's"} scope)
                </Link>
              </div>
            </div>
          </div>
        )}
        {/* Run summary header */}
        <div className="flex items-end justify-between gap-4 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-600">
              <Link to={`/dashboard/clients/${clientId}/utilities/fixed-assets${location.search || ""}`} className="hover:text-slate-900">Runs</Link>
              <span>›</span>
              <span>FY {run?.fy || "…"}</span>
            </div>
            <h1 className="font-heading text-2xl mt-1">{run?.name || "Fixed Assets Run"}</h1>
            <div className="text-[12px] text-slate-500 mt-1">
              FY {run?.fy_start} → {run?.fy_end} ·
              {" "}<span className="font-mono">{run?.source_filename || "no books ingested yet"}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="fa-open-history"
              onClick={() => setShowHistory(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 text-[12px] hover:bg-slate-50"
            >
              <History size={13}/> History
            </button>
            <button
              data-testid="fa-ingest-from-library"
              onClick={onIngestFromLibrary}
              disabled={busy || scopeMismatch}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-300 bg-emerald-50/60 text-emerald-800 text-[12px] hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={scopeMismatch
                ? "Disabled — URL scope does not match this run's scope."
                : "Pull Books JSON from the Data Library."}
            >
              <FolderUp size={13}/> Ingest from Library
            </button>
            {/* Depreciation Settings — shown on division-scoped runs, AND
                on single-entity clients (0 divisions) whose only run is
                "consolidation" scope by definition; backend resolves the
                toggle to a client-wide default key in that case. Hidden
                only for a TRUE multi-division client's rolled-up
                consolidation view, where one button can't represent
                each division's independent setting. */}
            {(run?.scope_kind === "division" || divisions.length === 0) && (
              <button
                data-testid="fa-open-division-settings"
                onClick={() => setShowAdSettings(true)}
                disabled={busy || scopeMismatch}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 text-[12px] hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Additional Depreciation (Sec 32(1)(iia)) eligibility for this division"
              >
                <Settings2 size={13}/> Depreciation Settings
              </button>
            )}
            {run?.scope_kind === "division" && (
              run?.divisional_status === "ready" || run?.divisional_status === "ready_no_activity" ? (
                <button
                  data-testid="fa-reopen-division"
                  onClick={reopenDivision}
                  disabled={busy || scopeMismatch}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 text-amber-900 text-[12px] hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={scopeMismatch
                    ? "Disabled — URL scope does not match this run's scope."
                    : `This division is marked ${run.divisional_status === "ready_no_activity" ? "'No Activity'" : "'Ready'"}. Click to reopen for editing.`}
                >
                  Reopen Division ({run.divisional_status === "ready_no_activity" ? "No Activity" : "Ready"})
                </button>
              ) : (
                <button
                  data-testid="fa-mark-division-complete"
                  onClick={markDivisionComplete}
                  disabled={busy || scopeMismatch}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-600 bg-emerald-600 text-white text-[12px] hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={scopeMismatch
                    ? "Disabled — URL scope does not match this run's scope."
                    : "Mark this division as final — Ready (with data) or No Activity (empty). The consolidation needs every division finalised before it can compose."}
                >
                  Mark Division Complete
                </button>
              )
            )}
            <BooksDrop dropRef={dropRef} onFile={scopeMismatch ? null : onIngest} busy={busy || scopeMismatch}/>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-5 gap-2 mb-6">
          <Stat label="FA Ledgers" value={summary.total_ledgers || 0}/>
          <Stat label="Unclassified" value={unclassifiedCount} accent="amber"/>
          <Stat label="Classified" value={classifiedCount} accent="emerald"/>
          <Stat label="Additions" value={summary.additions || 0}/>
          <Stat label="Credits to Review" value={summary.credits || 0} accent="rose"/>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-4 border-b border-[#E5E5E0]">
          {[
            ["ledgers",   "Ledgers",     BookOpen,    summary.total_ledgers || 0],
            ["credits",   "Credits",     ArrowDown,   summary.credits || 0],
            ["additions", "Additions",   FileText,    summary.additions || 0],
            ["compute",   "Compute",     Calculator,  null],
            ["summary",   "Summary",     LayoutGrid,  null],
          ].map(([id, label, Icon, count]) => (
            <button
              key={id}
              data-testid={`fa-tab-${id}`}
              onClick={() => { setAuditFilter(null); setTab(id); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] -mb-px border-b-2 ${
                tab === id ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={13}/>
              {label}
              {count !== null && (
                <span className={`text-[10.5px] font-mono px-1.5 py-0.5 ${tab === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "ledgers" && (
        /* Workbench */
        <div className="bg-white border border-[#E5E5E0]">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EDEDE7]">
            <div className="flex items-center gap-2">
              <BookOpen size={15} className="text-slate-600"/>
              <h2 className="font-heading text-base">Ledger Classification Workbench</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input
                  data-testid="fa-ledger-search"
                  className="pl-7 pr-2 py-1.5 text-[12px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700 w-56"
                  placeholder="Search ledger or subhead…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                data-testid="fa-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-2 py-1.5 text-[12px] border border-[#D4D4D0] focus:outline-none"
              >
                <option value="all">All ({ledgers.length})</option>
                <option value="unclassified">Unclassified ({unclassifiedCount})</option>
                <option value="classified">Classified ({classifiedCount})</option>
              </select>
              <select
                data-testid="fa-filter-tally-group"
                value={tallyGroupFilter}
                onChange={(e) => setTallyGroupFilter(e.target.value)}
                className="px-2 py-1.5 text-[12px] border border-[#D4D4D0] focus:outline-none max-w-[170px]"
                title="Filter by Tally Group"
              >
                <option value="all">Tally Group: All</option>
                {tallyGroupOptions.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select
                data-testid="fa-filter-subhead"
                value={subheadFilter}
                onChange={(e) => setSubheadFilter(e.target.value)}
                className="px-2 py-1.5 text-[12px] border border-[#D4D4D0] focus:outline-none max-w-[170px]"
                title="Filter by AssureAI Subhead"
              >
                <option value="all">Subhead: All</option>
                {subheadOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {mismatchCount > 0 && (
                <button
                  data-testid="fa-filter-mismatch-toggle"
                  onClick={() => setMismatchOnly(v => !v)}
                  className={`inline-flex items-center gap-1 px-2 py-1.5 text-[12px] border ${
                    mismatchOnly ? "bg-amber-500 text-white border-amber-500" : "border-amber-300 text-amber-700 hover:bg-amber-50"
                  }`}
                  title="Ledgers where the assigned IT Block disagrees with the firm's Ledger Mapping Subhead"
                >
                  <AlertTriangle size={12}/>
                  {mismatchCount} mismatch{mismatchCount === 1 ? "" : "es"}
                </button>
              )}
            </div>
          </div>

          {selectedIds.size > 0 && (
            <div data-testid="fa-bulk-action-bar" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white">
              <span className="text-[12.5px] font-medium" data-testid="fa-bulk-selected-count">
                {selectedIds.size} ledger{selectedIds.size === 1 ? "" : "s"} selected
              </span>
              <select
                data-testid="fa-bulk-block-select"
                value={bulkBlock}
                onChange={(e) => setBulkBlock(e.target.value)}
                className="ml-2 px-2 py-1.5 text-[12px] text-slate-900 border border-slate-300 focus:outline-none"
              >
                <option value="">— Map selected to IT Block —</option>
                {blocks.map(b => (
                  <option key={b.block_label} value={b.block_label}>{b.block_label} ({b.rate}%)</option>
                ))}
              </select>
              <button
                data-testid="fa-bulk-apply-btn"
                onClick={bulkApplyBlock}
                disabled={!bulkBlock || bulkApplying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed"
              >
                {bulkApplying ? <Loader2 size={12} className="animate-spin"/> : null}
                Apply
              </button>
              <button
                data-testid="fa-bulk-clear-selection-btn"
                onClick={() => setSelectedIds(new Set())}
                className="ml-1 px-2 py-1.5 text-[12px] text-slate-300 hover:text-white"
              >
                Clear selection
              </button>
            </div>
          )}

          {ledgers.length === 0 ? (
            <div className="p-10 text-center">
              <FolderUp size={26} className="mx-auto text-slate-400"/>
              <div className="mt-3 font-heading text-base">No ledgers ingested yet</div>
              <p className="text-[13px] text-[#52524E] mt-1">Drop your <span className="font-mono">Books.json</span> in the upload zone above.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left bg-[#F9F9F8] text-[11px] font-mono uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-2 w-8">
                      <input
                        type="checkbox"
                        data-testid="fa-select-all-visible"
                        checked={visibleLedgers.length > 0 && visibleLedgers.every(r => selectedIds.has(r.fa_ledger_id))}
                        onChange={toggleSelectAllVisible}
                        className="cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-2">Ledger</th>
                    <th className="px-3 py-2">Tally Group</th>
                    <th className="px-3 py-2">Subhead</th>
                    <th className="px-3 py-2 text-right">Closing</th>
                    <th className="px-3 py-2 text-center">Adds</th>
                    <th className="px-3 py-2 text-center">Credits</th>
                    <th className="px-3 py-2 w-[260px]">IT Block</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EDEDE7]">
                  {visibleLedgers.map(L => (
                    <tr key={L.fa_ledger_id} className={`hover:bg-[#FBFBF8] ${L.subhead_block_mismatch ? "bg-amber-50/60" : ""}`}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          data-testid={`fa-select-${L.fa_ledger_id}`}
                          checked={selectedIds.has(L.fa_ledger_id)}
                          onChange={() => toggleSelected(L.fa_ledger_id)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-2 font-medium" data-testid={`fa-led-${L.fa_ledger_id}`}>{L.name}</td>
                      <td className="px-3 py-2 text-slate-600">{L.parent_group}</td>
                      <td className="px-3 py-2" data-testid={`fa-led-subhead-${L.fa_ledger_id}`}>
                        {L.assureai_subhead ? (
                          <span className="text-slate-700">{L.assureai_subhead}</span>
                        ) : (
                          <span className="text-slate-400 italic">Not mapped</span>
                        )}
                        {L.subhead_block_mismatch && (
                          <span
                            data-testid={`fa-led-mismatch-${L.fa_ledger_id}`}
                            className="inline-flex items-center gap-0.5 ml-1.5 px-1 py-0.5 text-[10px] font-mono bg-amber-100 text-amber-800 border border-amber-300"
                            title={`Ledger Mapping says '${L.assureai_subhead}' — assigned IT Block '${L.block_label}' looks inconsistent. Verify.`}
                          >
                            <AlertTriangle size={9}/> mismatch
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{inr(L.closing_balance)}</td>
                      <td className="px-3 py-2 text-center">{L.addition_count || 0}</td>
                      <td className="px-3 py-2 text-center">{L.deletion_count || 0}</td>
                      <td className="px-3 py-2">
                        <BlockSelect
                          value={L.block_label}
                          status={L.classification_status}
                          options={blocks}
                          saving={savingFor === L.fa_ledger_id}
                          onChange={(v) => setBlock(L.fa_ledger_id, v)}
                          testid={`fa-block-${L.fa_ledger_id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {tab === "additions" && (
          <AdditionsTab
            rid={rid}
            blocks={blocks}
            auditFilter={auditFilter}
            onClearAuditFilter={() => setAuditFilter(null)}
          />
        )}
        {tab === "credits"   && <CreditsTab rid={rid} blocks={blocks}/>}
        {tab === "compute"   && <ComputeTab rid={rid}/>}
        {tab === "summary"   && (
          <SummaryTab rid={rid} onJumpToFlag={goToFilteredAdditions}/>
        )}
      </div>
      {showHistory && (
        <GenerationsDrawer
          open={showHistory}
          onClose={() => setShowHistory(false)}
          endpoint={`/fixed-assets/runs/${rid}`}
          moduleLabel="Fixed Assets · IT Depreciation"
          module="fixed_assets"
        />
      )}
      {showAdSettings && (
        <DivisionSettingsModal runId={rid} onClose={() => setShowAdSettings(false)}/>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */
function Header({ clientId }) {
  return (
    <div className="border-b border-[#E5E5E0] bg-white">
      <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-3">
        <Link to={`/dashboard/clients/${clientId}`} className="text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16}/>
        </Link>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">Fixed Assets — IT Depreciation</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "amber"   ? "text-amber-700" :
              accent === "rose"    ? "text-rose-700" :
              accent === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="bg-white border border-[#E5E5E0] px-3 py-2.5">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-heading mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function BlockSelect({ value, status, options, saving, onChange, testid }) {
  // Subtle visual hint: italic + amber border when the block was auto-suggested
  // (server flagged it but the auditor hasn't ratified it yet via the dropdown).
  const isAuto = status === "auto_suggested" && !!value;
  const isEmpty = !value;
  const cls = isEmpty
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : isAuto
      ? "border-sky-200 bg-sky-50 text-slate-900 italic"
      : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return (
    <div className="relative">
      <select
        data-testid={testid}
        value={value || ""}
        disabled={saving}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full pr-7 pl-2 py-1.5 text-[12.5px] border focus:outline-none focus:border-slate-700 ${cls}`}
      >
        <option value="">— Select block —</option>
        {options.map(b => (
          <option key={b.block_label} value={b.block_label}>
            {b.block_label} ({b.rate}%)
          </option>
        ))}
      </select>
      {saving && (
        <Loader2 size={11} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-slate-500"/>
      )}
    </div>
  );
}

function BooksDrop({ dropRef, onFile, busy }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      ref={dropRef}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const f = e.dataTransfer.files?.[0]; if (f) onFile(f);
      }}
      className={`border border-dashed px-3 py-2 cursor-pointer text-[12px] flex items-center gap-2 ${over ? "border-slate-700 bg-slate-50" : "border-slate-300 bg-white"}`}
      onClick={() => inputRef.current?.click()}
      data-testid="fa-books-drop"
    >
      {busy ? <Loader2 size={13} className="animate-spin"/> : <FolderUp size={13}/>}
      <span>Drop Books.json or click</span>
      <input
        ref={inputRef} type="file" accept=".json,.gz,.gzip"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
      />
    </div>
  );
}
